/**
 * IN-APP BROWSERS: which app a page is open inside, and the link that leaves it.
 *
 * A link tapped inside Instagram, Facebook, TikTok and the like opens in the
 * app's own browser, not the visitor's. On a phone that browser is locked
 * upright, and Moonfall is landscape-only on a phone, so the visitor sat on
 * "Rotate your device" for ever (owner's iPhone, 13 October 2026). It also has
 * no Google session of its own, so signing in there means typing a password
 * inside the app. The rotate screen therefore offers a way out to the
 * visitor's real browser (public/scripts/in-app-browser-flag.js).
 *
 * Pure: functions of strings. No document, no window, no storage, so the same
 * answers hold in the head of the page, in Jest and on a server.
 *
 * NOTHING HERE IS GUESSED. Each marker and each link is taken from a maintained
 * public source, and the tests carry those sources' captured user agents:
 *   inapp-spy       github.com/shalanah/inapp-spy       src/regexAppName.ts
 *   ua-parser-js    github.com/faisalman/ua-parser-js   src/main/ua-parser.js
 *   detect-inapp    github.com/f2etw/detect-inapp       src/inapp.js
 *   inapp-debugger  github.com/shalanah/inapp-debugger  README: which link left
 *                   which app, tested on devices (iPhone table of 7 March
 *                   2026, Android table of 7 November 2024)
 *   eiab            github.com/anaclumos/eiab           packages/eiab/src
 * None of the links is documented by the app that honours it, and an app
 * update can end any of them. That is why a link is offered only where a
 * device test reports it working (a button that does nothing is worse than
 * none), and why a tap that leaves the visitor on the page is answered with
 * the app-menu instruction (in-app-browser-flag.js).
 *
 * SEEN, AND ONLY READ. One link has been seen to work by us: Instagram on
 * iPhone, `instagram://extbrowser` from a tap, on the owner's own phone on 13
 * October 2026 (the button opened his browser). Every other marker and link in
 * this file is read from the sources above and has not been seen here.
 *
 * Contract: tests/unit/client/inAppBrowser.test.js.
 */

/* PHONES ONLY. A tablet turns, and so do its apps, so "this browser cannot
   turn" would be false there; a desktop never meets the rotate screen. Android
   phones carry "Mobile" in every Chromium string and tablets do not. An iPad
   says iPad (or, in desktop mode, Macintosh), never iPhone. */
const IOS_PHONE = /\b(?:iPhone|iPod)\b/;
const ANDROID_PHONE = /\bAndroid\b/;
const MOBILE_TOKEN = /\bMobile\b/;

/* The Meta family names itself with FB…/ fields; current Messenger builds send
   the same fields as Facebook and differ only in lacking IABMV (inapp-spy's
   rule, and the one its device table was measured with). */
const META_FIELD = /\bFB[\w_]+\//i;
const META_MESSENGER_FIELD = /\bFB[\w_]+\/Messenger/i;
const META_APP_FIELD = /FB_IAB|FBAN/i;
const META_IAB_VERSION = /\bIABMV/i;
const FACEBOOK_WORD = /\bFacebook/i;

/**
 * Markers in the order they are tried. ORDER IS PART OF THE CONTRACT:
 *   - Threads ("Barcelona" is its internal name) comes before Instagram,
 *     because its browser can name Instagram too, and a Threads visitor must
 *     never be handed Instagram's link.
 *   - Messenger comes before Facebook, whose fields it shares.
 * @type {ReadonlyArray<readonly [string, (userAgent: string) => boolean]>}
 */
const APPS = Object.freeze([
  ['threads', (ua) => /\bBarcelona/i.test(ua)],
  ['instagram', (ua) => /\bInstagram/i.test(ua)],
  ['messenger', (ua) => META_MESSENGER_FIELD.test(ua)
    || (META_APP_FIELD.test(ua) && !META_IAB_VERSION.test(ua))],
  ['facebook', (ua) => META_FIELD.test(ua) || FACEBOOK_WORD.test(ua)],
  // "musical_ly" and "trill" are the app's two package names.
  ['tiktok', (ua) => /musical_ly|Bytedance|\btrill_/i.test(ua)],
  ['snapchat', (ua) => /Snapchat/i.test(ua)],
  ['linkedin', (ua) => /LinkedInApp/i.test(ua)],
  ['line', (ua) => /\bLine\//i.test(ua)],
  ['wechat', (ua) => /\bMicroMessenger\//i.test(ua)],
]);

/**
 * Which phone this is: an iPhone, an Android phone, or neither (a tablet, a
 * desktop, anything that is not a string). The rotate screen names the phone's
 * own rotation lock with it (phone-os-flag.js).
 *
 * @param {unknown} userAgent `navigator.userAgent`
 * @returns {'ios' | 'android' | null}
 */
function phoneSystem(userAgent) {
  if (typeof userAgent !== 'string' || !userAgent) return null;
  if (IOS_PHONE.test(userAgent)) return 'ios';
  if (ANDROID_PHONE.test(userAgent) && MOBILE_TOKEN.test(userAgent)) return 'android';
  return null;
}

/**
 * The in-app browser this user agent belongs to, or null for a browser the
 * visitor chose (Safari, Chrome, Firefox, Brave, Samsung Internet…), a tablet,
 * a desktop, a web view with no app's name on it, or anything that is not a
 * string.
 *
 * @param {unknown} userAgent `navigator.userAgent`
 * @returns {{ app: string, os: 'ios' | 'android' } | null}
 */
function detectInAppBrowser(userAgent) {
  if (typeof userAgent !== 'string' || !userAgent) return null;
  const os = phoneSystem(userAgent);
  if (!os) return null;
  for (const [app, matches] of APPS) {
    if (matches(userAgent)) return { app, os };
  }
  return null;
}

/**
 * How each app is asked to open the visitor's own browser.
 *
 *   extbrowser  `<scheme>://extbrowser/?url=<the address, encoded>`: the app's
 *               own "open externally" link. Instagram ignores the Safari
 *               scheme (inapp-debugger: "use instagram workaround"; a real
 *               iPhone in github.com/BeeFans-Oficial/socialbee/pull/24), and
 *               answers this one from a tap with its own confirmation.
 *               SEEN TO OPEN THE BROWSER: the owner's iPhone, inside
 *               Instagram, 13 October 2026. Threads' `barcelona://` twin is
 *               read, not seen.
 *   safari      `x-safari-https://…`: Safari's own scheme on iOS 17 and later.
 *   intent      `intent://…#Intent;scheme=https;S.browser_fallback_url=…;end`:
 *               Android's default browser
 *               (developer.chrome.com/docs/android/intents).
 *
 * `open: 'window'` means the link must be opened with `window.open` from the
 * tap: Facebook on iPhone passes the Safari scheme on only that way
 * (inapp-debugger: "only via window.open").
 *
 * AN APP MISSING FROM ITS SYSTEM'S ROW HAS NO KNOWN LINK and gets the
 * instruction alone: TikTok, Snapchat and WeChat on iPhone and TikTok and
 * WeChat on Android are reported as passing nothing on; Snapchat and LinkedIn
 * on Android normally open the phone's browser themselves and were never
 * measured.
 *
 * RE-READ 17 OCTOBER 2026, for the paid ads (Instagram, Facebook, TikTok):
 *   - Instagram's `extbrowser` link: shown working on Instagram 445 on iOS 27
 *     and Android 16 (inapp-debugger pull 23, 8-10 September 2026), and on the
 *     owner's iPhone (13 October). The Safari scheme stopped working in
 *     Instagram 417 as a link and in 421 through a window (inapp-debugger
 *     issue 16, March 2026), which is why Instagram is not given it.
 *   - Facebook on iPhone: the Safari scheme through a window worked on 9 March
 *     2026 (same issue); a Facebook 555 build is reported to ignore it from a
 *     script (eiab issue 2), and the half-screen browser a Facebook video ad
 *     opens ignored it on Facebook 529-531 (inapp-spy issue 53). An ad's tap
 *     may therefore go nowhere, which is what the card's Copy link is for.
 *   - TikTok: no scheme is reported to leave it. iPhone: the Safari scheme and
 *     the browsers' own schemes both fail (inapp-debugger table, 7 March
 *     2026). Android: the intent is "not dependable" (its table, 7 November
 *     2024), and an Android web view that cannot follow an intent may show an
 *     error page in place of ours. Link-in-bio vendors claim an exit and show
 *     none. So TikTok has no link here: the card offers Copy link and the
 *     app's own menu.
 */
const WAYS_OUT = Object.freeze({
  ios: Object.freeze({
    instagram: Object.freeze({ kind: 'extbrowser', scheme: 'instagram', open: 'link' }),
    threads: Object.freeze({ kind: 'extbrowser', scheme: 'barcelona', open: 'link' }),
    facebook: Object.freeze({ kind: 'safari', open: 'window' }),
    messenger: Object.freeze({ kind: 'safari', open: 'link' }),
    linkedin: Object.freeze({ kind: 'safari', open: 'link' }),
    line: Object.freeze({ kind: 'safari', open: 'link' }),
  }),
  android: Object.freeze({
    instagram: Object.freeze({ kind: 'intent', open: 'link' }),
    threads: Object.freeze({ kind: 'intent', open: 'link' }),
    facebook: Object.freeze({ kind: 'intent', open: 'link' }),
    messenger: Object.freeze({ kind: 'intent', open: 'link' }),
    line: Object.freeze({ kind: 'intent', open: 'link' }),
  }),
});

function own(table, key) {
  return table && typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key)
    ? table[key]
    : null;
}

/**
 * Each app by the name it gives itself, spelled as the app spells it. A name
 * is the same in every language: it is never translated, and the sentence
 * around it is (see nameTheApp).
 *
 * The name is as good as the detection above and no better. Facebook and
 * Messenger send the same fields and are told apart by one token, so a build
 * that drops or gains it would be called by its sibling's name.
 */
const APP_NAMES = Object.freeze({
  instagram: 'Instagram',
  threads: 'Threads',
  facebook: 'Facebook',
  messenger: 'Messenger',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  linkedin: 'LinkedIn',
  line: 'LINE',
  wechat: 'WeChat',
});

/**
 * @param {unknown} app an app as detectInAppBrowser names it
 * @returns {string | null} what the app calls itself, or null for anything else
 */
function appName(app) {
  return own(APP_NAMES, app);
}

/* The place a sentence keeps for the name, in ui.json's own notation. */
const NAME_PLACE = /\{\{\s*app\s*\}\}/g;

/**
 * A language's sentence about an app, with the app's name set into it:
 * "The {{app}} browser isn't supported." becomes "The Instagram browser isn't
 * supported." The name does not sit in the same place in every language, so
 * the sentence is always built from that language's own line
 * (ui.json `game.rotateInAppUnsupported`), never by joining pieces. (The
 * English keeps the name clear of a possessive: "Threads's browser" was the
 * alternative.)
 *
 * @param {unknown} template the language's line, with `{{app}}` where the name goes
 * @param {unknown} app an app as detectInAppBrowser names it
 * @returns {string | null} the sentence, or null without a line or a known app
 */
function nameTheApp(template, app) {
  const name = appName(app);
  if (typeof template !== 'string' || !template || name === null) return null;
  // A function, so nothing in a name can be read as a replacement pattern.
  return template.replace(NAME_PLACE, () => name);
}

/* The place a sentence keeps for an address the visitor is asked to type. */
const ADDRESS_PLACE = /\{\{\s*address\s*\}\}/g;

/**
 * A language's sentence with an address set into it, the way nameTheApp sets
 * an app's name: "Type {{address}} into Safari or Chrome." becomes "Type
 * moonfall.io/play into Safari or Chrome." (ui.json
 * `game.rotateInAppCopyFailed`, the line the card shows when a copy failed).
 *
 * @param {unknown} template the language's line, with `{{address}}` where the address goes
 * @param {unknown} address what the visitor is asked to type
 * @returns {string | null} the sentence, or null without a line or an address
 */
function placeAddress(template, address) {
  if (typeof template !== 'string' || !template || typeof address !== 'string' || !address) return null;
  return template.replace(ADDRESS_PLACE, () => address);
}

/** The address as a parsed web URL, or null for anything that is not one. */
function webAddress(address) {
  if (typeof address !== 'string' || !address) return null;
  let url;
  try {
    url = new URL(address);
  } catch (_) {
    return null;
  }
  return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
}

/**
 * The link that asks this app to open `address` in the visitor's own browser,
 * or null when no link is known to work there.
 *
 * The address goes out exactly as it came in: every query parameter the
 * visitor arrived with (`utm_*`, `lang`, an invite code) and nothing added.
 * Pass the address as it was when the page was PARSED: attribution.js and the
 * invite and voucher modules strip their parameters from the address bar a
 * moment later, and what they keep lives in a storage the other browser will
 * never see.
 *
 * @param {{ app: string, os: string } | null | undefined} found from detectInAppBrowser
 * @param {unknown} address an absolute http(s) address
 * @returns {{ href: string, open: 'link' | 'window' } | null}
 */
function buildWayOut(found, address) {
  const way = own(own(WAYS_OUT, found && found.os), found && found.app);
  const url = way && webAddress(address);
  if (!url) return null;
  // `location.href` is already in this form, so this is the address unchanged.
  const whole = url.href;

  if (way.kind === 'extbrowser') {
    return { href: `${way.scheme}://extbrowser/?url=${encodeURIComponent(whole)}`, open: way.open };
  }

  if (way.kind === 'safari') {
    // "https://…" becomes "x-safari-https://…": the scheme gains a prefix and
    // the rest is byte for byte.
    return { href: `x-safari-${whole}`, open: way.open };
  }

  /* Android reads everything after the LAST "#" as the intent, so the page's
     own fragment is left out of the data part (no Moonfall arrival address has
     one that matters) and still rides, whole, in the fallback. */
  const scheme = url.protocol.slice(0, -1);
  return {
    href: `intent://${url.host}${url.pathname}${url.search}`
      + `#Intent;scheme=${scheme};S.browser_fallback_url=${encodeURIComponent(whole)};end`,
    open: way.open,
  };
}

export { detectInAppBrowser, buildWayOut, appName, nameTheApp, phoneSystem, placeAddress };
